import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { getHighLevelContext } from '@/utils/highlevel';
import toast from 'react-hot-toast';

export function useXeroAuth() {
  const queryClient = useQueryClient();
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  // Listen for OAuth success messages from popup
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Handle OAuth success message from popup
      if (event.data?.type === 'OAUTH_SUCCESS' && event.data?.provider === 'xero') {
        console.log('Received Xero OAuth success message:', event.data);
        // Invalidate and refetch the connection status
        queryClient.invalidateQueries({
          queryKey: ['xero-connection', locationId],
        });
        toast.success('Successfully connected to Xero!');

        // Auto-refresh Xero entity counts after successful connection
        try {
          await apiClient.refreshEntityCounts(locationId, { system: 'xero' });
          queryClient.invalidateQueries({ queryKey: ['sync-status', locationId] });
          console.log('Xero entity counts refreshed after connection');
        } catch (err) {
          console.warn('Failed to refresh Xero counts after connection:', err);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [queryClient, locationId]);

  // Get connection status
  const {
    data: connection,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['xero-connection', locationId],
    queryFn: () => apiClient.getXeroConnection(locationId),
    enabled: !!locationId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Connect to Xero
  const connectMutation = useMutation({
    mutationFn: async () => {
      const url = await apiClient.getXeroAuthUrl(locationId);
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        url,
        'Xero OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Poll for popup close
      return new Promise<void>((resolve, reject) => {
        const checkPopup = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkPopup);
            // Refetch connection status
            queryClient.invalidateQueries({
              queryKey: ['xero-connection', locationId],
            });
            resolve();
          }
        }, 500);

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(checkPopup);
          if (popup && !popup.closed) {
            popup.close();
          }
          reject(new Error('OAuth timeout'));
        }, 300000);
      });
    },
    onSuccess: () => {
      // Toast is now shown by the message handler
    },
    onError: (error) => {
      toast.error(`Failed to connect: ${error.message}`);
    },
  });

  // Disconnect from Xero
  const disconnectMutation = useMutation({
    mutationFn: () => apiClient.disconnectXero(locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['xero-connection', locationId],
      });
      toast.success('Disconnected from Xero');
    },
    onError: (error) => {
      toast.error(`Failed to disconnect: ${error.message}`);
    },
  });

  return {
    connection,
    isConnected: !!connection?.isConnected,
    isLoading,
    error,
    connect: connectMutation.mutate,
    disconnect: disconnectMutation.mutate,
    isConnecting: connectMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
  };
}
